import { apiClient } from "./client";
import type {
  AssignTicketRequest,
  AttachmentDownloadUrl,
  CreateTicketRequest,
  InitTicketAttachmentRequest,
  InitTicketAttachmentResponse,
  ReplyToTicketRequest,
  SoftDeleteTicketResult,
  SupportAnalyticsFilters,
  SupportAnalyticsSummary,
  SupportAnalyticsTrend,
  SupportAnalyticsTrendFilters,
  SupportTicketDetail,
  SupportTicketFilters,
  SupportTicketPage,
  TicketAssignee,
  UpdateTicketStatusRequest,
} from "../types/supportTicket";

const BASE_URL = "/support-management";

const listParams = (filters: SupportTicketFilters) => ({
  page: filters.page ?? 0,
  pageSize: filters.pageSize ?? 25,
  ticketStatus: filters.ticketStatus,
  type: filters.type,
  priority: filters.priority,
  assigneeUserId: filters.assigneeUserId,
  unassignedOnly: filters.unassignedOnly,
  companyTenantId: filters.companyTenantId,
  unreadOnly: filters.unreadOnly,
  search: filters.search,
});

export const supportTicketsApi = {
  createTicket: async (
    ticket: CreateTicketRequest,
  ): Promise<SupportTicketDetail> => {
    const response = await apiClient.post<SupportTicketDetail>(
      `${BASE_URL}/tickets`,
      ticket,
    );
    return response.data;
  },

  getTickets: async (
    filters: SupportTicketFilters = {},
  ): Promise<SupportTicketPage> => {
    const response = await apiClient.get<SupportTicketPage>(
      `${BASE_URL}/tickets`,
      { params: listParams(filters) },
    );
    return response.data;
  },

  getTriageTickets: async (
    filters: SupportTicketFilters = {},
  ): Promise<SupportTicketPage> => {
    const response = await apiClient.get<SupportTicketPage>(
      `${BASE_URL}/triage/tickets`,
      { params: listParams({ ticketStatus: "OPEN", ...filters }) },
    );
    return response.data;
  },

  getTicket: async (id: number): Promise<SupportTicketDetail> => {
    const response = await apiClient.get<SupportTicketDetail>(
      `${BASE_URL}/tickets/${id}`,
    );
    return response.data;
  },

  replyToTicket: async (
    id: number,
    reply: ReplyToTicketRequest,
  ): Promise<SupportTicketDetail> => {
    const response = await apiClient.post<SupportTicketDetail>(
      `${BASE_URL}/tickets/${id}/replies`,
      reply,
    );
    return response.data;
  },

  updateTicketStatus: async (
    id: number,
    request: UpdateTicketStatusRequest,
  ): Promise<SupportTicketDetail> => {
    const response = await apiClient.patch<SupportTicketDetail>(
      `${BASE_URL}/tickets/${id}/ticket-status`,
      request,
    );
    return response.data;
  },

  assignTicket: async (
    id: number,
    request: AssignTicketRequest,
  ): Promise<SupportTicketDetail> => {
    const response = await apiClient.patch<SupportTicketDetail>(
      `${BASE_URL}/tickets/${id}/assignee`,
      request,
    );
    return response.data;
  },

  getAssignees: async (): Promise<TicketAssignee[]> => {
    const response = await apiClient.get<TicketAssignee[]>(
      `${BASE_URL}/triage/assignees`,
    );
    return response.data;
  },

  deleteTicket: async (id: number): Promise<SoftDeleteTicketResult> => {
    const response = await apiClient.delete<SoftDeleteTicketResult>(
      `${BASE_URL}/tickets/${id}`,
    );
    return response.data;
  },

  getAttachmentDownloadUrl: async (
    ticketId: number,
    attachmentId: number,
  ): Promise<AttachmentDownloadUrl> => {
    const response = await apiClient.get<AttachmentDownloadUrl>(
      `${BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}/download`,
    );
    return response.data;
  },

  /** Step 1: Init a presigned upload for one ticket/reply attachment. */
  initAttachment: async (
    ticketId: number,
    request: InitTicketAttachmentRequest,
  ): Promise<InitTicketAttachmentResponse> => {
    const response = await apiClient.post<InitTicketAttachmentResponse>(
      `${BASE_URL}/tickets/${ticketId}/attachments`,
      request,
    );
    return response.data;
  },

  /** Step 2: Upload the raw file bytes directly to S3 using the presigned URL. */
  uploadToS3: (
    uploadUrl: string,
    file: File,
    contentType: string,
    onProgress?: (progress: number) => void,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress((event.loaded / event.total) * 100);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(
            new Error(
              `S3 upload failed with status ${xhr.status}: ${xhr.statusText}`,
            ),
          );
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during S3 upload"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("S3 upload was aborted"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", contentType);
      xhr.send(file);
    });
  },

  /** Step 3: Mark the upload complete so it appears on the ticket. */
  completeAttachment: async (
    ticketId: number,
    attachmentId: number,
    sizeBytes?: number,
  ): Promise<void> => {
    await apiClient.patch(
      `${BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}/complete`,
      sizeBytes !== undefined ? { sizeBytes } : {},
    );
  },

  /** Init → PUT → complete for a single attachment. */
  uploadAttachment: async (
    ticketId: number,
    file: File,
    messageId?: number,
    onProgress?: (progress: number) => void,
  ): Promise<number> => {
    const contentType = file.type || "application/octet-stream";
    const init = await supportTicketsApi.initAttachment(ticketId, {
      fileName: file.name,
      contentType,
      sizeBytes: file.size,
      messageId,
    });
    await supportTicketsApi.uploadToS3(
      init.uploadUrl,
      file,
      contentType,
      onProgress,
    );
    await supportTicketsApi.completeAttachment(
      ticketId,
      init.attachmentId,
      file.size,
    );
    return init.attachmentId;
  },

  getAnalyticsSummary: async (
    filters: SupportAnalyticsFilters,
  ): Promise<SupportAnalyticsSummary> => {
    const response = await apiClient.get<SupportAnalyticsSummary>(
      `${BASE_URL}/analytics/summary`,
      {
        params: { fromDate: filters.fromDate, toDate: filters.toDate },
      },
    );
    return response.data;
  },

  getAnalyticsTrend: async (
    filters: SupportAnalyticsTrendFilters,
  ): Promise<SupportAnalyticsTrend> => {
    const response = await apiClient.get<SupportAnalyticsTrend>(
      `${BASE_URL}/analytics/trend`,
      {
        params: {
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          bucket: filters.bucket,
        },
      },
    );
    return response.data;
  },
};
